import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle } from "lucide-react";
import type { PlayProps } from "@/types/puzzle";

export default function StrokeOrderPlay({ data, onAnswer, result }: PlayProps) {
  const { t } = useTranslation();
  const character = (data.character as string) ?? "";
  const pinyin = (data.pinyin as string) ?? "";
  const meaning = (data.meaning as string) ?? "";
  const strokes = (data.strokes as string[]) ?? [];
  const medians = (data.medians as number[][][]) ?? [];

  const answered = result !== null;

  const [completedCount, setCompletedCount] = useState(0);
  const [animatingIndex, setAnimatingIndex] = useState<number | null>(null);
  const [wrongTap, setWrongTap] = useState<number | null>(null);
  // Every tap (in order, including out-of-order ones) — the server replays this
  // sequence to grade order + leniency (PuzzleGrader#stroke_order_correct?).
  const [taps, setTaps] = useState<number[]>([]);
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);

  const strokeIndices = useMemo(
    () => shuffle(strokes.map((_, i) => i)),
    [strokes],
  );

  // When the student finishes all strokes in order, submit the tap sequence for
  // server grading.
  useEffect(() => {
    if (!answered && strokes.length > 0 && completedCount === strokes.length) {
      onAnswer(taps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCount]);

  // Animate stroke drawing when a stroke is tapped in the right order.
  useEffect(() => {
    if (animatingIndex === null) return;
    const path = pathRefs.current[animatingIndex];
    if (!path) return;

    const length = path.getTotalLength();
    path.style.strokeDasharray = `${length}`;
    path.style.strokeDashoffset = `${length}`;
    path.style.transition = "none";

    path.getBoundingClientRect();
    path.style.transition = "stroke-dashoffset 0.5s ease-in-out";
    path.style.strokeDashoffset = "0";
  }, [animatingIndex]);

  const handleTap = (strokeIndex: number) => {
    if (answered || animatingIndex !== null) return;
    setTaps((prev) => [...prev, strokeIndex]);

    if (strokeIndex === completedCount) {
      // Correct stroke order — animate it in.
      setAnimatingIndex(strokeIndex);
      const newCount = completedCount + 1;

      setTimeout(() => {
        setCompletedCount(newCount);
        setAnimatingIndex(null);
      }, 550);
    } else {
      // Out-of-order tap — flash it red, but don't grade (PR 6).
      setWrongTap(strokeIndex);
      setTimeout(() => setWrongTap(null), 600);
    }
  };

  // Give up on this one — submit whatever was tapped (an incomplete sequence
  // grades as incorrect server-side).
  const handleSkip = () => {
    if (answered) return;
    onAnswer(taps);
  };

  return (
    <div className="space-y-6 max-w-lg mx-auto">
      <div className="text-center animate-fade-in-up">
        <h2 className="text-lg font-medium text-muted-foreground">
          {t("puzzleStudent.strokeOrder.instruction")}
        </h2>
        <p className="text-2xl font-bold mt-1">
          {character}
          <span className="text-base font-normal text-muted-foreground ml-2">
            {pinyin} — {meaning}
          </span>
        </p>
      </div>

      {/* Character SVG */}
      <div className="flex justify-center">
        <svg
          viewBox="0 0 1024 1024"
          className="w-48 h-48 sm:w-56 sm:h-56 border-2 border-border rounded-xl bg-secondary/20"
        >
          <g transform="scale(1, -1) translate(0, -900)">
            {/* Grid guides */}
            <line x1="512" y1="0" x2="512" y2="900" stroke="currentColor" strokeWidth={1} opacity={0.06} />
            <line x1="0" y1="450" x2="1024" y2="450" stroke="currentColor" strokeWidth={1} opacity={0.06} />

            {/* Faded outlines */}
            {strokes.map((d, i) => (
              <path key={`bg-${i}`} d={d} fill="currentColor" opacity={0.08} />
            ))}

            {/* Clip paths for stroke shapes */}
            <defs>
              {strokes.map((d, i) => (
                <clipPath key={`clip-${i}`} id={`play-clip-${i}`}>
                  <path d={d} />
                </clipPath>
              ))}
            </defs>

            {/* Completed strokes */}
            {strokes.map((d, i) => {
              if (i >= completedCount && i !== animatingIndex) return null;
              if (i === animatingIndex) {
                return (
                  <g key={`anim-${i}`} clipPath={`url(#play-clip-${i})`}>
                    <path
                      ref={(el) => { pathRefs.current[i] = el; }}
                      d={medianToPath(medians[i])}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={200}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity={0.85}
                    />
                  </g>
                );
              }
              return <path key={`done-${i}`} d={d} fill="currentColor" opacity={0.85} />;
            })}
          </g>
        </svg>
      </div>

      {/* Stroke buttons */}
      <div className="flex flex-wrap gap-3 justify-center">
        {strokeIndices.map((strokeIdx) => {
          const isDone = strokeIdx < completedCount;
          const isAnimating = strokeIdx === animatingIndex;
          const isWrong = strokeIdx === wrongTap;

          return (
            <button
              key={strokeIdx}
              onClick={() => handleTap(strokeIdx)}
              disabled={isDone || answered}
              className={`w-14 h-14 rounded-lg border-b-4 transition-all active:scale-95 enabled:hover:-translate-y-0.5 flex items-center justify-center ${
                isDone || isAnimating
                  ? "bg-green-500/15 border-2 border-green-500/40"
                  : isWrong
                    ? "bg-red-500/15 border-2 border-red-500/40 animate-shake"
                    : "bg-secondary border-2 border-border hover:bg-secondary/80"
              }`}
            >
              {isDone ? (
                <CheckCircle className="w-5 h-5 text-green-700" />
              ) : (
                <svg viewBox="0 0 1024 1024" className="w-10 h-10">
                  <g transform="scale(1, -1) translate(0, -900)">
                    <path
                      d={strokes[strokeIdx]}
                      fill="currentColor"
                      opacity={0.75}
                    />
                  </g>
                </svg>
              )}
            </button>
          );
        })}
      </div>

      {/* Progress */}
      <p className="text-xs text-muted-foreground text-center">
        {t("puzzleStudent.strokeOrder.progress", {
          completed: completedCount,
          total: strokes.length,
        })}
      </p>

      {!answered && (
        <Button variant="outline" onClick={handleSkip} className="w-full h-11">
          {t("puzzleStudent.strokeOrder.skip")}
        </Button>
      )}

      {answered && (
        <div
          className={`flex items-center gap-3 p-4 rounded-lg animate-fade-in-up ${
            result?.correct ? "bg-green-500/10 text-green-700" : "bg-red-500/10 text-red-700"
          }`}
        >
          {result?.correct ? (
            <CheckCircle className="w-5 h-5 shrink-0 animate-bounce-in" />
          ) : (
            <XCircle className="w-5 h-5 shrink-0 animate-shake" />
          )}
          <span className="text-sm font-semibold">
            {result?.correct
              ? t("puzzleStudent.strokeOrder.feedbackCorrect")
              : t("puzzleStudent.strokeOrder.feedbackIncorrect")}
          </span>
        </div>
      )}
    </div>
  );
}

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function medianToPath(points: number[][]): string {
  if (!points || points.length === 0) return "";
  const [first, ...rest] = points;
  return `M ${first[0]} ${first[1]} ${rest.map((p) => `L ${p[0]} ${p[1]}`).join(" ")}`;
}
